import { Mail, CheckCircle, XCircle, Clock } from 'lucide-react';
import { EmailLogStatus } from '@/types/email-log.types';

type TabType = 'all' | EmailLogStatus;

interface Tab {
  key: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const tabs: Tab[] = [
  { key: 'all', label: 'All', icon: Mail },
  { key: 'sent', label: 'Sent', icon: CheckCircle },
  { key: 'failed', label: 'Failed', icon: XCircle },
  { key: 'pending', label: 'Pending', icon: Clock },
];

interface EmailLogTabsProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  counts?: {
    all?: number;
    sent?: number;
    delivered?: number;
    failed?: number;
    pending?: number;
    retry_scheduled?: number;
    skipped?: number;
    bounced?: number;
  };
}

function formatCount(count?: number): string {
  if (count === undefined || count === null) return '';
  return count.toLocaleString();
}

export default function EmailLogTabs({
  activeTab,
  onTabChange,
  counts = {},
}: EmailLogTabsProps) {
  const getCount = (key: TabType): number | undefined => {
    if (key === 'all') {
      // Prefer the authoritative all-status total from metrics (includes skipped, and
      // archived when the toggle is on) so the "All" tab always matches the list total.
      if (counts.all !== undefined) return counts.all;
      return (
        (counts.sent || 0) +
        (counts.delivered || 0) +
        (counts.failed || 0) +
        (counts.pending || 0) +
        (counts.retry_scheduled || 0) +
        (counts.skipped || 0) +
        (counts.bounced || 0)
      );
    }
    if (key === 'sent') {
      return (counts.sent || 0) + (counts.delivered || 0);
    }
    if (key === 'pending') {
      return (counts.pending || 0) + (counts.retry_scheduled || 0);
    }
    return counts[key];
  };

  return (
    <div className="flex border-b border-gray-200">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key;
        const count = getCount(tab.key);
        const Icon = tab.icon;

        return (
          <button
            key={tab.key}
            onClick={() => onTabChange(tab.key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              isActive
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span>{tab.label}</span>
            {count !== undefined && count > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  isActive
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {formatCount(count)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export type { TabType };
